import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Button, CircularProgress } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import { getTokens } from "../../styles/tokens";
import { getBackendUrl } from "../../config";
import { i18n } from "../../translate/i18n";
import { classifyMetaEvent } from "./signupEvents";

// Popup do Embedded Signup da Meta. Abre SEMPRE em host da plataforma (único domínio autorizado no
// app Meta) e recebe o token do opener por postMessage — por isso usa um axios próprio, sem os
// interceptors de sessão (o localStorage deste host pode ser de outro usuário).
const FB_ORIGINS = ["https://www.facebook.com", "https://web.facebook.com"];
const MSG = "digiteai:meta-signup:";

// ?coex=1 → coexistência: o número continua no app WhatsApp Business do celular.
const COEX = typeof window !== "undefined" && new URLSearchParams(window.location.search).get("coex") === "1";

const SDK_TIMEOUT_MS = 20000;   // sdk.js bloqueado (adblock) → erro em vez de spinner eterno
const IDS_TIMEOUT_MS = 25000;   // code chegou mas o evento FINISH não → erro (código vale 30 s)

const loadSdk = (appId, version) =>
  new Promise((resolve, reject) => {
    if (window.FB) return resolve(window.FB);
    const timer = setTimeout(() => reject(new Error("sdk_timeout")), SDK_TIMEOUT_MS);
    window.fbAsyncInit = () => {
      clearTimeout(timer);
      window.FB.init({ appId, autoLogAppEvents: true, xfbml: false, version });
      resolve(window.FB);
    };
    const s = document.createElement("script");
    s.src = "https://connect.facebook.net/pt_BR/sdk.js";
    s.async = true; s.defer = true; s.crossOrigin = "anonymous";
    s.onerror = () => { clearTimeout(timer); reject(new Error("sdk_blocked")); };
    document.body.appendChild(s);
  });

// Página de popup: um cartão central com o estado da conexão e nada mais.
const useStyles = makeStyles((theme) => {
  const t = getTokens(theme.palette.type, theme.palette.primary.main);
  return {
    root: {
      minHeight: "100vh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: 16,
      boxSizing: "border-box",
      fontFamily: t.font,
      backgroundColor: t.page,
      color: t.text,
    },
    card: {
      width: "100%",
      maxWidth: 440,
      padding: "32px 28px",
      boxSizing: "border-box",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      textAlign: "center",
      backgroundColor: t.paper,
      border: `1px solid ${t.line}`,
      borderRadius: t.radius.md,
      boxShadow: t.shadow.soft,
    },
    icon: {
      width: 48,
      height: 48,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      borderRadius: t.radius.md,
      backgroundColor: t.tint.soft,
      color: t.brand,
      "& .MuiSvgIcon-root": { fontSize: 24 },
    },
    iconOk: { backgroundColor: t.status.ok.bg, color: t.status.ok.fg },
    iconDown: { backgroundColor: t.status.down.bg, color: t.status.down.fg },
    iconWarn: { backgroundColor: t.status.warn.bg, color: t.status.warn.fg },
    title: {
      margin: "16px 0 0",
      fontSize: 18,
      fontWeight: 600,
      lineHeight: 1.35,
      letterSpacing: "-0.01em",
      color: t.text,
    },
    text: {
      margin: "8px 0 0",
      minHeight: 21,
      fontSize: 14,
      lineHeight: 1.5,
      color: t.textSecondary,
      overflowWrap: "anywhere",
    },
    action: { marginTop: 24 },
  };
});

const MetaSignup = () => {
  const classes = useStyles();
  const [phase, setPhase] = useState("waiting"); // waiting | loading | ready | fb | working | done | cancelled | error | notEnabled
  const [message, setMessage] = useState("");
  const [result, setResult] = useState(null);
  const init = useRef(null);   // { token, companyId, origin }
  const cfg = useRef(null);
  const client = useRef(null);
  const signup = useRef({ code: null, ids: null });
  const idsTimer = useRef(null);

  const reply = payload => {
    if (window.opener && init.current) window.opener.postMessage(payload, init.current.origin);
  };

  const tryOnboard = async () => {
    const { code, ids } = signup.current;
    if (!code || !ids) return; // precisa dos dois: o código vem do FB.login, os ids do evento da Meta
    signup.current = { code: null, ids: null };
    clearTimeout(idsTimer.current);
    setPhase("working");
    try {
      const { data } = await client.current.post("/meta-signup/onboard", { code, ...ids, ...(COEX ? { coexistence: true } : {}) });
      setResult(data); setPhase("done");
      reply({ type: `${MSG}done`, ...data });
    } catch (err) {
      const key = err?.response?.data?.error;
      const known = key && i18n.exists(`metaSignup.errors.${key}`);
      // erros humanos do backend (limite do plano, nome repetido…) são mostrados como vieram
      const raw = typeof key === "string" && !/^ERR_/.test(key) ? key : null;
      setPhase("error"); setMessage(known ? i18n.t(`metaSignup.errors.${key}`) : raw || i18n.t("metaSignup.errors.generic"));
      reply({ type: `${MSG}error`, message: key || "generic" });
    }
  };

  // 1) handshake com o opener: só aceita init cuja origem declarada bate com event.origin (https)
  useEffect(() => {
    const onMsg = async event => {
      const d = event.data;
      if (!d || d.type !== `${MSG}init` || init.current) return;
      if (typeof d.origin !== "string" || d.origin !== event.origin || !/^https:\/\//.test(d.origin)) return;
      if (typeof d.token !== "string" || !d.token) return;
      if (window.opener && event.source !== window.opener) return; // só a janela que abriu o popup
      init.current = { token: d.token, companyId: d.companyId, origin: d.origin };
      client.current = axios.create({ baseURL: getBackendUrl(), headers: { Authorization: `Bearer ${d.token}` } });
      reply({ type: `${MSG}ack` });
      setPhase("loading");
      try {
        const { data } = await client.current.get("/meta-signup/config");
        if (!data.enabled) return setPhase("notEnabled");
        cfg.current = data;
        await loadSdk(data.appId, data.graphVersion);
        setPhase("ready");
      } catch (e) {
        setPhase("error");
        setMessage(i18n.t(/^sdk_/.test(e?.message) ? "metaSignup.errors.sdk" : "metaSignup.errors.generic"));
      }
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, []);

  // 2) eventos do Embedded Signup (ids da WABA/número) chegam por message da própria Meta
  useEffect(() => {
    const onMeta = event => {
      if (!FB_ORIGINS.includes(event.origin)) return;
      const ev = classifyMetaEvent(event.data);
      if (!ev) return;
      if (ev.kind === "finish") {
        signup.current.ids = ev.ids;
        tryOnboard();
      } else if (ev.kind === "only_waba") {
        clearTimeout(idsTimer.current); signup.current = { code: null, ids: null };
        setPhase("error"); setMessage(i18n.t("metaSignup.errors.onlyWaba"));
      } else if (ev.kind === "cancel") {
        clearTimeout(idsTimer.current); signup.current = { code: null, ids: null };
        setPhase("cancelled"); reply({ type: `${MSG}cancel` });
      } else if (ev.kind === "error") {
        clearTimeout(idsTimer.current); signup.current = { code: null, ids: null };
        setPhase("error"); setMessage(ev.message || i18n.t("metaSignup.errors.generic"));
      }
    };
    window.addEventListener("message", onMeta);
    return () => window.removeEventListener("message", onMeta);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const launch = () => {
    setPhase("fb");
    window.FB.login(
      response => {
        const code = response?.authResponse?.code;
        if (!code) { setPhase("cancelled"); reply({ type: `${MSG}cancel` }); return; }
        signup.current.code = code;
        // se o evento FINISH (ids) não chegar, não fica em spinner: o código expira em 30 s
        clearTimeout(idsTimer.current);
        idsTimer.current = setTimeout(() => {
          if (signup.current.code && !signup.current.ids) {
            signup.current = { code: null, ids: null };
            setPhase("error"); setMessage(i18n.t("metaSignup.errors.noIds"));
          }
        }, IDS_TIMEOUT_MS);
        tryOnboard();
      },
      { config_id: cfg.current.configId, response_type: "code", override_default_response_type: true, extras: { setup: {}, featureType: COEX ? "whatsapp_business_app_onboarding" : "", sessionInfoVersion: "3" } }
    );
  };

  const busy = ["waiting", "loading", "fb", "working"].includes(phase);
  const failed = phase === "error" || phase === "notEnabled";
  const iconClass = [
    classes.icon,
    phase === "done" ? classes.iconOk : null,
    failed ? classes.iconDown : null,
    phase === "cancelled" ? classes.iconWarn : null,
  ]
    .filter(Boolean)
    .join(" ");
  return (
    <div className={classes.root}>
      <div className={classes.card}>
        <div className={iconClass}>
          {busy ? (
            <CircularProgress size={22} color="inherit" />
          ) : phase === "done" ? (
            <CheckCircleOutlineIcon />
          ) : failed || phase === "cancelled" ? (
            <ErrorOutlineIcon />
          ) : (
            <WhatsAppIcon />
          )}
        </div>
        <h1 className={classes.title}>{i18n.t(COEX ? "metaSignup.coexTitle" : "metaSignup.title")}</h1>
        <p className={classes.text} role="status" aria-live="polite">
          {phase === "waiting" && i18n.t("metaSignup.waiting")}
          {phase === "loading" && i18n.t("metaSignup.loadingSdk")}
          {phase === "ready" && i18n.t(COEX ? "metaSignup.coexReady" : "metaSignup.ready")}
          {phase === "fb" && i18n.t("metaSignup.fb")}
          {phase === "working" && i18n.t("metaSignup.working")}
          {phase === "done" && `${i18n.t("metaSignup.done")} ${i18n.t(`metaSignup.status.${result?.status || "ok"}`)}`}
          {phase === "cancelled" && i18n.t("metaSignup.cancelled")}
          {phase === "notEnabled" && i18n.t("metaSignup.notEnabled")}
          {phase === "error" && message}
        </p>
        {(phase === "ready" || phase === "error" || phase === "cancelled") && window.FB && cfg.current && (
          <Button variant="contained" color="primary" className={classes.action} onClick={launch}>
            {i18n.t("metaSignup.button")}
          </Button>
        )}
        {phase === "done" && (
          <Button variant="outlined" className={classes.action} onClick={() => window.close()}>
            OK
          </Button>
        )}
      </div>
    </div>
  );
};

export default MetaSignup;
